import { api } from './client';

export type LocalAgentPlatformKey = 'windows' | 'macos';
export type LocalAgentDownloadStatus = 'available' | 'unavailable';

export interface LocalAgentPlatformDownload {
  platform: LocalAgentPlatformKey;
  label: string;
  arch: string;
  version: string;
  file_name: string;
  download_url: string;
  sha256: string;
  size_bytes: number;
  status: LocalAgentDownloadStatus;
}

export interface LocalAgentDownloadManifest {
  product_name: string;
  version: string;
  updated_at: string;
  release_notes_url: string;
  skill_center_bootstrap_url: string;
  platforms: Record<LocalAgentPlatformKey, LocalAgentPlatformDownload>;
}

export type LocalAgentReleaseStatus = 'published' | 'retired';

export interface LocalAgentRelease {
  id: string;
  version: string;
  platform: LocalAgentPlatformKey;
  arch: string;
  file_name: string;
  s3_key: string;
  public_url: string;
  sha256: string;
  size_bytes: number;
  status: LocalAgentReleaseStatus;
  release_notes_url: string;
  published_at: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface LocalAgentReleaseList {
  list: LocalAgentRelease[];
  total: number;
}

interface ApiResp<T> {
  code: number;
  message: string;
  data: T;
}

export const localAgentApi = {
  async getDownloads(): Promise<LocalAgentDownloadManifest> {
    const res = await api.get<ApiResp<LocalAgentDownloadManifest>>('/local-agent/downloads');
    return res.data;
  },

  async listReleases(limit = 30): Promise<LocalAgentReleaseList> {
    const res = await api.get<ApiResp<LocalAgentReleaseList>>('/local-agent/releases', {
      params: { limit },
    });
    return res.data;
  },

  async publishRelease(params: {
    version: string;
    platform: LocalAgentPlatformKey;
    arch: string;
    release_notes_url?: string;
    file: File;
    onProgress?: (loaded: number, total?: number) => void;
  }): Promise<LocalAgentRelease> {
    const form = new FormData();
    form.append('version', params.version);
    form.append('platform', params.platform);
    form.append('arch', params.arch || 'x64');
    if (params.release_notes_url) {
      form.append('release_notes_url', params.release_notes_url);
    }
    form.append('file', params.file);
    const res = await api.post<ApiResp<LocalAgentRelease>>('/local-agent/releases', form, {
      // Installers are intentionally large. Upload speed varies significantly
      // across offices/VPNs, so do not abort a healthy request on the client.
      // The gateway owns the one-hour safety timeout for this route.
      timeout: 0,
      onUploadProgress: (event) => params.onProgress?.(event.loaded, event.total),
    });
    return res.data;
  },

  async retireRelease(id: string): Promise<LocalAgentRelease> {
    const res = await api.delete<ApiResp<LocalAgentRelease>>(`/local-agent/releases/${id}`);
    return res.data;
  },
};
